/**
 * Caption line-break styles, persisted in `line_break_style` (DatoCMS
 * `lineBreakStyle` on caption templates). Unset, rows break by
 * `transcript_maximum_length`. A Punctuation style overrides that length:
 * rows break only at punctuation and pauses, with no width limit.
 * `punctuation` breaks at commas and sentence ends; `punctuation_no_commas`
 * at sentence ends only.
 */
export const LINE_BREAK_STYLES = [
  'punctuation',
  'punctuation_no_commas',
] as const;

export type LineBreakStyle = (typeof LINE_BREAK_STYLES)[number];

/** The segmenter's name for a Punctuation style. */
export type PunctuationLineBreak = 'withCommas' | 'sentencesOnly';

export function isLineBreakStyle(value: unknown): value is LineBreakStyle {
  return value === 'punctuation' || value === 'punctuation_no_commas';
}

/** The Punctuation style a persisted `line_break_style` selects, if any. */
export function resolvePunctuationLineBreak(
  style: string | null | undefined,
): PunctuationLineBreak | undefined {
  switch (style) {
    case 'punctuation':
      return 'withCommas';
    case 'punctuation_no_commas':
      return 'sentencesOnly';
    default:
      return undefined;
  }
}

/**
 * Fallback line width (visual width) for full-width text under a Punctuation
 * style when the caption box can't be measured — the segmenter's default line
 * length (`MAX_SEGMENT_CHAR_LENGTH`), kept here so the model layer can read it
 * without importing the segmenter (which imports `KaraokeClip`).
 */
export const PUNCTUATION_WIDE_LINE_WIDTH = 36;

/** Headroom for glyph-width variance and stroke. */
const LINE_FILL_RATIO = 0.95;

/**
 * A full-width glyph's advance on the canvas, in ems. Measured on the preview
 * canvas at two box widths: Japanese renders at ~1.07–1.12× the configured
 * font size, so assuming 1em over-filled lines (the renderer re-wrapped their
 * last word) and more than this leaves them short of the box.
 */
const FULL_WIDTH_GLYPH_EM = 1.12;

/**
 * Caption box width (percent of the canvas) for full-width Punctuation rows.
 * Templates default to 76%, which leaves the broken lines short of the frame;
 * spaceless rows the canvas can't wrap get the wider box instead.
 */
const PUNCTUATION_WIDE_BOX_PERCENT = 90;

/**
 * The box width a full-width Punctuation row renders in: at least
 * `PUNCTUATION_WIDE_BOX_PERCENT` when the template sets a narrower percentage,
 * otherwise the template's own width.
 */
export function widenedCaptionBoxWidth<T>(width: T): T | string {
  if (typeof width !== 'string' || !width.trim().endsWith('%')) return width;
  return parseFloat(width) < PUNCTUATION_WIDE_BOX_PERCENT
    ? `${PUNCTUATION_WIDE_BOX_PERCENT}%`
    : width;
}

/**
 * How much full-width text fits on one line of a caption box, in visual width
 * units (a full-width character counts 2): the box width over the advance of
 * one full-width glyph (`FULL_WIDTH_GLYPH_EM` plus letter spacing, a
 * percentage of the font size). `undefined` when a size is in a unit this
 * can't resolve.
 */
export function wideCaptionLineCapacity(
  config: {
    width?: string | number | null;
    font_size?: string | number | null;
    letter_spacing?: string | number | null;
  },
  canvasSize: { width: number; height: number },
): number | undefined {
  const boxWidth = toPixels(config.width, canvasSize, canvasSize.width);
  const fontSize = toPixels(config.font_size, canvasSize, canvasSize.height);
  if (!boxWidth || !fontSize) return undefined;
  const letterSpacing = parseFloat(String(config.letter_spacing ?? 0)) || 0;
  const glyphAdvance = fontSize * (FULL_WIDTH_GLYPH_EM + letterSpacing / 100);
  return Math.floor(((boxWidth * LINE_FILL_RATIO) / glyphAdvance) * 2);
}

/** A Creatomate length in pixels; `%` resolves against `percentBase`. */
function toPixels(
  value: string | number | null | undefined,
  canvasSize: { width: number; height: number },
  percentBase: number,
): number | undefined {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return undefined;
  const amount = parseFloat(value);
  if (!Number.isFinite(amount) || amount <= 0) return undefined;
  const unit = value.match(/[a-z%]+/i)?.[0] ?? 'px';
  const { width, height } = canvasSize;
  switch (unit) {
    case 'px':
      return amount;
    case '%':
      return (amount / 100) * percentBase;
    case 'vw':
      return (amount / 100) * width;
    case 'vh':
      return (amount / 100) * height;
    case 'vmin':
      return (amount / 100) * Math.min(width, height);
    case 'vmax':
      return (amount / 100) * Math.max(width, height);
    default:
      return undefined;
  }
}
